import { Button, Flex, Form, Input, Modal, Select } from 'antd';
import React, { useMemo, useState } from 'react';
import { useTransferJourneyByOperator } from 'services/journeys';
import { GeolocationType } from 'interfaces/journey';
import { SwapOutlined } from '@ant-design/icons';
import { useJourneyDashboardContext } from '../context';

interface TransferRouteButtonProps {
  journeyId?: string;
  disabled?: boolean;
}

const geolocationOptions = [
  { value: GeolocationType.CustomAddress, label: 'Dirección Personalizada' },
  { value: GeolocationType.DistributionCenter, label: 'PaQuery Point' },
];

const TransferRouteButton = React.forwardRef<
  HTMLButtonElement,
  TransferRouteButtonProps
>(({ disabled, journeyId }, ref) => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [geolocationType, setGeolocationType] = useState<GeolocationType>(
    GeolocationType.CustomAddress,
  );
  const [address, setAddress] = useState('');
  const { drivers, driver: currentDriver } = useJourneyDashboardContext();
  const { mutateAsync: transferRoute, isPending } =
    useTransferJourneyByOperator();

  const driverOptions = useMemo(
    () =>
      (drivers ?? [])
        .filter((d) => d.active && d.id !== currentDriver?.id)
        .map((d) => ({ value: d.id, label: `${d.name} ${d.lastName}` })),
    [drivers, currentDriver],
  );

  const newDriverId = Form.useWatch('newDriverId', form);
  const needsAddress = geolocationType === GeolocationType.CustomAddress;
  const canSubmit =
    !!newDriverId && (!needsAddress || !!address.trim()) && !isPending;

  if (!journeyId) return null;

  const reset = () => {
    form.resetFields();
    setGeolocationType(GeolocationType.DistributionCenter);
    setAddress('');
  };

  const handleOk = async () => {
    await form.validateFields();
    await transferRoute({ journeyId, newDriverId, geolocationType, address });
    reset();
    setOpen(false);
  };

  const handleCancel = () => {
    reset();
    setOpen(false);
  };

  return (
    <>
      <Modal
        destroyOnHidden
        open={open}
        onOk={handleOk}
        onCancel={handleCancel}
        title="Transferir ruta"
        okText="Transferir"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !canSubmit }}
        confirmLoading={isPending}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 24 }}>
            Seleccione el driver al cual transferir la ruta, y la dirección de
            origen de la nueva ruta, donde se encuentran los paquetes a retirar.
          </p>
          <Form.Item name="newDriverId" label="Driver">
            <Select
              placeholder="Seleccioná un driver"
              options={driverOptions}
              showSearch
              filterOption={(input: string, option: any) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item label="Dirección de origen">
            <Flex>
              <Select
                value={geolocationType}
                options={geolocationOptions}
                onChange={(v) => {
                  setGeolocationType(v);
                  setAddress('');
                }}
              />
              {needsAddress && (
                <Input
                  placeholder="Ingrese la dirección"
                  style={{ marginLeft: '8px' }}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              )}
            </Flex>
          </Form.Item>
        </Form>
      </Modal>
      <Button
        ref={ref}
        size="small"
        icon={<SwapOutlined />}
        disabled={disabled || isPending}
        onClick={() => setOpen(true)}
      >
        Transferir ruta
      </Button>
    </>
  );
});

export default TransferRouteButton;
